import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Pencil, Trash2, Users } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ClassTemplate } from '@/hooks/useClassTemplates';
import { useClassTemplateMutations } from '@/hooks/useClassTemplateMutations';
import { ClassTemplateFormDialog } from './ClassTemplateFormDialog';

interface ClassTemplatesListProps {
  templates: ClassTemplate[];
  isLoading: boolean;
  weekday: number;
}

export function ClassTemplatesList({ templates, isLoading, weekday }: ClassTemplatesListProps) {
  const [editingTemplate, setEditingTemplate] = useState<ClassTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<ClassTemplate | null>(null);
  const [activatingPausedTemplate, setActivatingPausedTemplate] = useState<ClassTemplate | null>(null);
  const { deleteTemplate, toggleActive } = useClassTemplateMutations();

  const handleDelete = async () => {
    if (deletingTemplate) {
      await deleteTemplate.mutateAsync({ id: deletingTemplate.id, teacher_code: deletingTemplate.teacher_code });
      setDeletingTemplate(null);
    }
  };

  const handleToggleActive = async (template: ClassTemplate) => {
    // ถ้ากำลังเปลี่ยนจาก inactive → active และ class นั้นอยู่ในสถานะพักสอน
    // ให้ถาม user ก่อนว่าจะปรับเป็นสอนปกติด้วยมั้ย
    if (!template.is_active && template.status === 'PAUSED') {
      setActivatingPausedTemplate(template);
      return;
    }

    await toggleActive.mutateAsync({
      id: template.id,
      is_active: !template.is_active,
      teacher_code: template.teacher_code,
    });
  };

  const handleActivateOnly = async () => {
    if (!activatingPausedTemplate) return;
    await toggleActive.mutateAsync({
      id: activatingPausedTemplate.id,
      is_active: true,
      teacher_code: activatingPausedTemplate.teacher_code,
    });
    setActivatingPausedTemplate(null);
  };

  const handleActivateAndResume = async () => {
    if (!activatingPausedTemplate) return;
    await toggleActive.mutateAsync({
      id: activatingPausedTemplate.id,
      is_active: true,
      teacher_code: activatingPausedTemplate.teacher_code,
      status: 'NORMAL',
    });
    setActivatingPausedTemplate(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-muted-foreground">กำลังโหลด...</div>
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Users className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium">ไม่มีตารางเรียน</h3>
        <p className="text-muted-foreground">ยังไม่มีตารางเรียนสำหรับวันนี้</p>
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>เวลา</TableHead>
            <TableHead>ครู</TableHead>
            <TableHead>วิชา</TableHead>
            <TableHead>สถานที่</TableHead>
            <TableHead>ประเภท</TableHead>
            <TableHead>นักเรียน</TableHead>
            <TableHead>สถานะ</TableHead>
            <TableHead className="text-right">จัดการ</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {templates.map((template) => (
            <TableRow key={template.id}>
              <TableCell className="font-medium">
                {template.start_time.slice(0, 5)} - {template.end_time.slice(0, 5)}
                <div className="text-xs text-muted-foreground">
                  {template.duration_minutes} นาที
                </div>
                {template.status === 'PAUSED' && (
                  <Badge variant="destructive" className="mt-1 w-fit">
                    พักสอน
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                <div>{template.teacher_code} {template.teacher_nickname}</div>
                <div className="text-xs text-muted-foreground">
                  {template.teacher_name}
                </div>
              </TableCell>
              <TableCell>
                <div>{template.subject}</div>
                <div className="text-xs text-muted-foreground">
                  {template.education_level}
                </div>
              </TableCell>
              <TableCell>
                {template.location}
                {template.room_name && (
                  <span className="text-muted-foreground"> / {template.room_name}</span>
                )}
              </TableCell>
              <TableCell>
                <div className="flex flex-col gap-1">
                  <Badge variant="outline" className="w-fit">
                    {template.course_type}
                  </Badge>
                  <Badge variant="secondary" className="w-fit">
                    {template.class_type}
                  </Badge>
                </div>
              </TableCell>
              <TableCell>
                {template.students && template.students.length > 0 ? (
                  <div className="space-y-2">
                    {template.students.map((student, index) => (
                      <div key={student.code || index}>
                        <div>{student.code} {student.nickname}</div>
                        <div className="text-xs text-muted-foreground">
                          {student.first_name} {student.last_name}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>
                <Switch
                  checked={template.is_active}
                  onCheckedChange={() => handleToggleActive(template)}
                />
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setEditingTemplate(template)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDeletingTemplate(template)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ClassTemplateFormDialog
        open={!!editingTemplate}
        onOpenChange={(open) => !open && setEditingTemplate(null)}
        template={editingTemplate}
        defaultWeekday={weekday}
      />

      <AlertDialog open={!!deletingTemplate} onOpenChange={(open) => !open && setDeletingTemplate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการลบ</AlertDialogTitle>
            <AlertDialogDescription>
              คุณแน่ใจหรือไม่ว่าต้องการลบตารางเรียนนี้? การกระทำนี้ไม่สามารถเลิกทำได้
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              ลบ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!activatingPausedTemplate}
        onOpenChange={(open) => !open && setActivatingPausedTemplate(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ตารางเรียนนี้กำลังพักสอนอยู่</AlertDialogTitle>
            <AlertDialogDescription>
              คุณกำลังเปิดใช้งานตารางเรียนที่อยู่ในสถานะ "พักสอน"
              ต้องการปรับจาก "พักสอน" เป็น "สอนปกติ" ด้วยหรือไม่?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleActivateOnly}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/80"
            >
              เปิดใช้งานเท่านั้น (ยังพักสอน)
            </AlertDialogAction>
            <AlertDialogAction onClick={handleActivateAndResume}>
              เปิดใช้งาน + สอนปกติ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
